import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScanLine, Loader2, AlertTriangle, CheckCircle2, ImageOff, RefreshCw, Edit3,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useTripOcrResult,
  useRetriggerOcr,
} from '@/hooks/useOcr';
import { ConfidencePill } from './ConfidencePill';
import { OcrReviewDialog } from './OcrReviewDialog';

export function TripOcrSection({ tripId }: { tripId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useTripOcrResult(tripId);
  const retrigger = useRetriggerOcr();
  const [reviewing, setReviewing] = useState(false);

  if (isLoading) {
    return (
      <div className="rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('ocr.inline.loading')}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-md border border-dashed border-neutral-300 p-3 dark:border-neutral-700">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <ScanLine className="h-4 w-4" />
            {t('ocr.inline.no_ocr_yet')}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => retrigger.mutate(tripId)}
            disabled={retrigger.isPending}
          >
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${retrigger.isPending ? 'animate-spin' : ''}`} />
            {t('ocr.inline.run_ocr')}
          </Button>
        </div>
      </div>
    );
  }

  const isPending = data.status === 'pending' || data.status === 'processing';
  const isFailed = data.status === 'failed';
  const isReview = data.status === 'review_needed';

  return (
    <>
      <div className="space-y-3 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <StatusIcon status={data.status} />
            <span className="text-sm font-medium">
              {t('ocr.inline.title')}
            </span>
            <span className="text-xs text-neutral-500">
              · {t(`ocr.status.${data.status}`)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {(isFailed || isReview) && (
              <Button size="sm" variant="ghost" onClick={() => setReviewing(true)}>
                <Edit3 className="mr-1 h-3.5 w-3.5" />
                {t('ocr.inline.review')}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => retrigger.mutate(tripId)}
              disabled={retrigger.isPending}
              title={t('ocr.inline.run_again')}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${retrigger.isPending ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {isPending ? (
          <div className="text-xs text-neutral-500">{t('ocr.inline.processing')}</div>
        ) : isFailed ? (
          <div className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {data.failure_reason
              ? t(`ocr.failure_reason.${data.failure_reason}`)
              : t('ocr.failure_reason.other')}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <FieldCell label={t('ocr.field.m3')}             value={data.m3_value}        confidence={data.m3_confidence} />
            <FieldCell label={t('ocr.field.voucher_number')} value={data.voucher_number} confidence={data.voucher_confidence} />
            <FieldCell label={t('ocr.field.time')}           value={data.ocr_time}       confidence={data.time_confidence} />
            <FieldCell label={t('ocr.field.date')}           value={data.ocr_date}       confidence={data.date_confidence}
                       badge={data.date_source === 'exif' ? t('ocr.from_exif') : undefined} />
          </div>
        )}
      </div>

      <OcrReviewDialog
        ocrResultId={reviewing ? data.id : null}
        onClose={() => setReviewing(false)}
        onProcessed={() => setReviewing(false)}
      />
    </>
  );
}

function FieldCell({
  label, value, confidence, badge,
}: { label: string; value: string | null; confidence: string | null; badge?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded bg-neutral-50 p-2 text-xs dark:bg-neutral-900">
      <div className="flex items-center justify-between">
        <span className="text-neutral-500">{label}</span>
        <ConfidencePill confidence={confidence} compact />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono">{value || <span className="text-neutral-400">—</span>}</span>
        {badge && (
          <span className="rounded bg-blue-100 px-1 py-0.5 text-[9px] text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'pending' || status === 'processing') {
    return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
  }
  if (status === 'failed') {
    return <ImageOff className="h-4 w-4 text-red-500" />;
  }
  if (status === 'review_needed') {
    return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  }
  return <CheckCircle2 className="h-4 w-4 text-green-500" />;
}
